import { ref } from 'vue'
import { defineStore } from 'pinia'
import { clientApi } from '../api/client.api'
import { useFilterStore } from '@/modules/filter/stores/filter.store'
import { useQueryStore } from '@/modules/services/query/stores/query.store'
import { ROUTE_NAMES } from '@/router/constants/route.constants'
import {
  CLIENT_VIEW_SETTINGS,
  DASHBOARD_CLIENT_SETTINGS,
  MOTION_CLIENT_SETTINGS,
} from '@/modules/clients/constants/client.constants'
import { ActionType } from '@/modules/action/types/action.enums'
import { ClientSetting, ViewType } from '../types/client.enums'
import type { RouteRecordNameGeneric } from 'vue-router'
import type {
  ClientAction,
  ClientCarrier,
  ClientCountry,
  ClientDimension,
  ClientServiceGroup,
  ClientView,
  ClientZipZone,
} from '@/modules/clients/models/client.models'

export const useClientStore = defineStore('client', () => {
  const filterStore = useFilterStore()
  const queryStore = useQueryStore()

  const activeViewId = ref<ViewType>()
  const defaultViewId = ref<ViewType>()
  const views = ref<ClientView[]>([])
  const actions = ref<ClientAction[]>([])
  const actionGroups = ref<ClientAction[]>([])
  const carriers = ref<ClientCarrier[]>([])
  const countries = ref<ClientCountry[]>([])
  const serviceGroups = ref<ClientServiceGroup[]>([])
  const zipZones = ref<ClientZipZone[]>([])
  const settings = ref<Record<number, string>>({})
  const dateDisplayName = ref<string>('0')
  const dimensions = ref<Record<number, ClientDimension | undefined>>({
    1: undefined,
    2: undefined,
    3: undefined,
  })

  const getSettingRequestByRoute = (routeName: RouteRecordNameGeneric): number[] | null => {
    switch (routeName) {
      case ROUTE_NAMES.MOTION_VIEW:
        return MOTION_CLIENT_SETTINGS
      case ROUTE_NAMES.DASHBOARD:
        return DASHBOARD_CLIENT_SETTINGS
      default:
        return null
    }
  }

  const loadClientSettings = async (routeName: RouteRecordNameGeneric): Promise<void> => {
    const request = getSettingRequestByRoute(routeName)
    if (!request) return

    const { data } = await clientApi.getClientSettings(request)
    if (!data) return

    settings.value = data
    dateDisplayName.value = data[3111] ?? '0'
  }

  const isClientViewsReady = async (): Promise<boolean> => {
    const { data } = await clientApi.getClientViews()
    if (!data) return false

    views.value = data.views
    defaultViewId.value = data.activeViewId

    const viewName = queryStore.getViewName()
    const view = data.views.find((view) => view.viewName.toLowerCase() === viewName)

    activeViewId.value = view ? view.viewId : data.activeViewId

    return true
  }

  const loadClientActions = async (): Promise<void> => {
    const { data } = await clientApi.getClientActions()
    if (!data) return

    actions.value = data
    actionGroups.value = data.filter((action) => action.typeId === ActionType.ActionGroup)
  }

  const loadClientCarriers = async (): Promise<void> => {
    const { data } = await clientApi.getClientCarriers()
    if (!data) return

    carriers.value = data

    if (data.length === 1) filterStore.filter.carrierId = data[0].id
  }

  const loadClientCountries = async (): Promise<void> => {
    const { data } = await clientApi.getClientCountries()
    if (!data) return

    countries.value = data

    if (data.length === 1) filterStore.filter.countryId = data[0].id
  }

  const loadClientDimensions = async (): Promise<void> => {
    const { data } = await clientApi.getClientDimensions()
    if (!data) return

    dimensions.value = data

    Object.values(data).forEach((dimension) => {
      if (dimension.values.length === 1 && !filterStore.filter.dimensions[dimension.index]) {
        filterStore.filter.dimensions[dimension.index] = dimension.values[0].id
      }
    })
  }

  const loadClientServiceGroups = async (): Promise<void> => {
    const { data } = await clientApi.getClientServiceGroups()
    if (!data) return

    serviceGroups.value = data

    if (data.length === 1) filterStore.filter.serviceGroupId = data[0].id
  }

  const loadClientZipZones = async (): Promise<void> => {
    const { data } = await clientApi.getClientZipZones()
    if (!data) return

    zipZones.value = data

    if (data.length === 1) filterStore.filter.zipZoneId = data[0].id
  }

  const getActiveViewSettings = (): { [key: number]: ClientSetting } | undefined => {
    if (!activeViewId.value) return undefined

    return CLIENT_VIEW_SETTINGS[activeViewId.value]
  }

  const getSettingValue = (clientSetting: ClientSetting): string | undefined => {
    const viewSettings = getActiveViewSettings()

    if (!viewSettings) return undefined

    for (const [key, value] of Object.entries(viewSettings)) {
      if (value === clientSetting) {
        return settings.value[Number(key)]
      }
    }

    return undefined
  }

  const isSettingEnabled = (clientSetting: ClientSetting): boolean => {
    if (!activeViewId.value) return false

    const viewSettings = getActiveViewSettings()

    if (!viewSettings) return false

    for (const [key, value] of Object.entries(viewSettings)) {
      if (value === clientSetting) {
        return settings.value[Number(key)] === '1'
      }
    }

    return true
  }

  return {
    dateDisplayName,
    activeViewId,
    defaultViewId,
    views,
    actions,
    actionGroups,
    carriers,
    countries,
    dimensions,
    serviceGroups,
    zipZones,
    isClientViewsReady,
    loadClientActions,
    loadClientCarriers,
    loadClientCountries,
    loadClientDimensions,
    loadClientServiceGroups,
    loadClientZipZones,
    loadClientSettings,
    isSettingEnabled,
    getSettingValue,
  }
})
